/**
 * Converts a hex color string to an RGBA color object.
 *
 * @param {string} hexColor - The hex color string to convert.
 * @returns {object} The RGBA color object.
 */
export function hexToRgba(hexColor) {
	if (!hexColor) {
		// console.warn("helpers > hexToRgba() -> 'missing required input'");
		return hexColor;
	}

	const red = parseInt(hexColor.substring(1, 3), 16);
	const green = parseInt(hexColor.substring(3, 5), 16);
	const blue = parseInt(hexColor.substring(5, 7), 16);
	return { r: red, g: green, b: blue, a: 255 };
}

/**
 * Converts an RGBA color object to a hex color string.
 *
 * @param {object} rgbaColor - The RGBA color object to convert.
 * @returns {string} The hex color string.
 */
export function rgbaToHex(rgbaColor) {
	if (!rgbaColor) {
		// console.warn("helpers > rgbaToHex() -> 'missing required input'");
		return rgbaColor;
	}

	const red = rgbaColor.r.toString(16).padStart(2, "0");
	const green = rgbaColor.g.toString(16).padStart(2, "0");
	const blue = rgbaColor.b.toString(16).padStart(2, "0");
	return "#" + red + green + blue;
}

/**
 * Interpolates a color between two RGBA colors based on a percentage value.
 *
 * @param {object} color1 - The first RGBA color.
 * @param {object} color2 - The second RGBA color.
 * @param {number} percentage - The percentage value between 0 and 1.
 * @returns {object} The interpolated RGBA color.
 */
export function interpolateColor(color1, color2, percentage = 1) {
	if (!color1 || !color2) {
		return color1;
	}

	const red = Math.round((1 - percentage) * color1.r + percentage * color2.r);
	const green = Math.round((1 - percentage) * color1.g + percentage * color2.g);
	const blue = Math.round((1 - percentage) * color1.b + percentage * color2.b);

	return { r: red, g: green, b: blue, a: 255 };
}

/**
 * Returns a color value along a color gradient based on a percentage value.
 *
 * @param {number} percent - The percentage value between 0 and 100.
 * @param {string[]} gradient - The color gradient represented as an array of hex color strings.
 * @returns {string} The hex color string.
 */

export function getGradientColor(percent, gradient) {
	const colorCount = gradient.length;
	const clampedPercent = Math.max(0, Math.min(100, percent));
	const index = (clampedPercent / 100) * (colorCount - 1);

	const percentage = index % 1;
	const colorIndex1 = Math.floor(index);
	const colorIndex2 = Math.ceil(index);

	const rgbaColor1 = hexToRgba(gradient[colorIndex1]);
	const rgbaColor2 = hexToRgba(gradient[colorIndex2]);

	const interpolatedColor = interpolateColor(rgbaColor1, rgbaColor2, percentage);

	return rgbaToHex(interpolatedColor);
}
